import { useCallback, useEffect, useRef, useState } from 'react';

export interface GpsPositie {
  latitude: number;
  longitude: number;
  nauwkeurigheidM: number;
}

interface GeolocationResultaat {
  positie: GpsPositie | null;
  bezig: boolean;
  fout: string | null;
  /** Eenmalige bepaling, bijv. bij het indrukken van de GPS-knop. */
  bepaal: () => void;
  /** Blijft de positie volgen (voor gebruik onderweg). */
  volgen: boolean;
  zetVolgen: (aan: boolean) => void;
}

const FOUTTEKSTEN: Record<number, string> = {
  1: 'Geen toestemming voor locatiebepaling. Sta dit toe in de browserinstellingen.',
  2: 'Locatie is niet beschikbaar. Probeer het buiten opnieuw.',
  3: 'Locatiebepaling duurde te lang.',
};

/** GPS-positie van de coach, voor de "waar ben ik"-knop op de kaart. */
export function useGeolocation(): GeolocationResultaat {
  const [positie, setPositie] = useState<GpsPositie | null>(null);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const [volgen, zetVolgen] = useState(false);
  const watchId = useRef<number | null>(null);

  const verwerk = useCallback((resultaat: GeolocationPosition) => {
    setPositie({
      latitude: resultaat.coords.latitude,
      longitude: resultaat.coords.longitude,
      nauwkeurigheidM: resultaat.coords.accuracy,
    });
    setFout(null);
    setBezig(false);
  }, []);

  const verwerkFout = useCallback((error: GeolocationPositionError) => {
    setFout(FOUTTEKSTEN[error.code] ?? 'Locatie kon niet worden bepaald.');
    setBezig(false);
  }, []);

  const bepaal = useCallback(() => {
    if (!('geolocation' in navigator)) {
      setFout('Deze browser ondersteunt geen locatiebepaling.');
      return;
    }
    setBezig(true);
    navigator.geolocation.getCurrentPosition(verwerk, verwerkFout, {
      enableHighAccuracy: true,
      timeout: 12_000,
      maximumAge: 5_000,
    });
  }, [verwerk, verwerkFout]);

  useEffect(() => {
    if (!volgen || !('geolocation' in navigator)) return;

    watchId.current = navigator.geolocation.watchPosition(verwerk, verwerkFout, {
      enableHighAccuracy: true,
      maximumAge: 5_000,
    });

    return () => {
      if (watchId.current !== null) navigator.geolocation.clearWatch(watchId.current);
      watchId.current = null;
    };
  }, [volgen, verwerk, verwerkFout]);

  return { positie, bezig, fout, bepaal, volgen, zetVolgen };
}
